import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, Share2, Download } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import ReactMarkdown from 'react-markdown';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="page">
        <section className="section">
          <div className="container" style={{textAlign: 'center', padding: '100px 20px'}}>
            <h1 className="heading-1">Article Not Found</h1>
            <p className="body-large" style={{marginBottom: '30px'}}>The blog post you're looking for doesn't exist.</p>
            <Link to="/blog" className="btn-primary">Back to Blog</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Article Header */}
      <section className="section" style={{paddingTop: '40px', paddingBottom: '40px', background: '#fafafa'}}>
        <div className="container">
          <div style={{maxWidth: '800px', margin: '0 auto'}}>
            <Link to="/blog" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#00897b',
              fontSize: '15px',
              fontWeight: '600',
              textDecoration: 'none',
              marginBottom: '25px'
            }}>
              <ArrowLeft size={16} />
              Back to Blog
            </Link>
            
            <div style={{marginBottom: '15px'}}>
              <span style={{
                background: '#00897b',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>{post.category}</span>
            </div>
            
            <h1 style={{fontSize: '44px', fontWeight: '700', marginBottom: '20px', color: '#1a1a1a', lineHeight: '1.2'}}>
              {post.title}
            </h1>
            
            <div style={{display: 'flex', gap: '25px', marginBottom: '25px', flexWrap: 'wrap', color: '#555', fontSize: '15px'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <Calendar size={16} />
                <span>{new Date(post.publishDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <Clock size={16} />
                <span>{post.readTime}</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <Tag size={16} />
                <span>{post.author}</span>
              </div>
            </div>
            
            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
              {post.tags.map(tag => (
                <span key={tag} style={{
                  background: 'white',
                  color: '#666',
                  padding: '6px 14px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  border: '1px solid #e0e0e0'
                }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="section" style={{paddingTop: '50px', paddingBottom: '50px'}}>
        <div className="container">
          <article style={{
            maxWidth: '800px',
            margin: '0 auto',
            fontSize: '17px',
            lineHeight: '1.8',
            color: '#333'
          }}>
            <ReactMarkdown
              components={{
                h2: ({node, ...props}) => <h2 style={{fontSize: '32px', fontWeight: '700', marginTop: '50px', marginBottom: '20px', color: '#1a1a1a'}} {...props} />,
                h3: ({node, ...props}) => <h3 style={{fontSize: '24px', fontWeight: '700', marginTop: '35px', marginBottom: '15px', color: '#1a1a1a'}} {...props} />,
                h4: ({node, ...props}) => <h4 style={{fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '12px', color: '#333'}} {...props} />,
                p: ({node, ...props}) => <p style={{marginBottom: '20px', lineHeight: '1.8'}} {...props} />,
                ul: ({node, ...props}) => <ul style={{marginBottom: '20px', paddingLeft: '25px'}} {...props} />,
                ol: ({node, ...props}) => <ol style={{marginBottom: '20px', paddingLeft: '25px'}} {...props} />,
                li: ({node, ...props}) => <li style={{marginBottom: '10px'}} {...props} />,
                strong: ({node, ...props}) => <strong style={{fontWeight: '700', color: '#1a1a1a'}} {...props} />,
                table: ({node, ...props}) => (
                  <div style={{overflowX: 'auto', marginBottom: '30px'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '20px'}} {...props} />
                  </div>
                ),
                thead: ({node, ...props}) => <thead style={{background: '#f5f5f5'}} {...props} />,
                th: ({node, ...props}) => <th style={{padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: '600'}} {...props} />,
                td: ({node, ...props}) => <td style={{padding: '12px', borderBottom: '1px solid #eee'}} {...props} />,
                blockquote: ({node, ...props}) => (
                  <blockquote style={{
                    borderLeft: '4px solid #00897b',
                    paddingLeft: '20px',
                    marginLeft: '0',
                    marginBottom: '25px',
                    color: '#555',
                    fontStyle: 'italic'
                  }} {...props} />
                ),
                hr: ({node, ...props}) => <hr style={{border: 'none', borderTop: '1px solid #e0e0e0', margin: '40px 0'}} {...props} />,
                a: ({node, ...props}) => <a style={{color: '#00897b', textDecoration: 'underline'}} {...props} />
              }}
            >
              {post.content}
            </ReactMarkdown>
          </article>
        </div>
      </section>

      {/* Article Footer - Related Actions */}
      <section className="section" style={{background: '#fafafa', paddingTop: '50px', paddingBottom: '50px'}}>
        <div className="container">
          <div style={{maxWidth: '800px', margin: '0 auto'}}>
            <h3 style={{fontSize: '24px', fontWeight: '700', marginBottom: '25px', textAlign: 'center'}}>Take Action</h3>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px'}}>
              <Link to="/oh4/calculator.html" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                padding: '25px',
                background: 'white',
                borderRadius: '10px',
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                textAlign: 'center'
              }}>
                <div style={{fontSize: '16px', fontWeight: '600', color: '#1a1a1a'}}>Calculate Your Savings</div>
                <div style={{fontSize: '14px', color: '#666'}}>Get a custom ROI analysis</div>
              </Link>
              
              <Link to="/contact" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                padding: '25px',
                background: '#00897b',
                color: 'white',
                borderRadius: '10px',
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(0,137,123,0.3)',
                textAlign: 'center'
              }}>
                <div style={{fontSize: '16px', fontWeight: '600'}}>Book Free Audit</div>
                <div style={{fontSize: '14px', opacity: '0.9'}}>Talk to our engineers</div>
              </Link>
              
              <a href="/downloads/radisson-case-study.html" target="_blank" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                padding: '25px',
                background: 'white',
                borderRadius: '10px',
                textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                textAlign: 'center',
                color: '#333'
              }}>
                <Download size={20} />
                <div style={{fontSize: '16px', fontWeight: '600', color: '#1a1a1a'}}>Download PDF</div>
                <div style={{fontSize: '14px', color: '#666'}}>Full case study report</div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
